// ═══════════════════════════════════════════════════════════════════════════
// commandDispatch/submitCommandText.ts — command-line router orchestrator
// (D5: App.tsx → command-registry unification).
// ═══════════════════════════════════════════════════════════════════════════
//
// Extracted verbatim from App.tsx's inline `submitCommandText` (~1500
// lines, 112 literal `upperStart === "ALIAS"` comparisons). The prelude
// (colour-prompt consume / empty guard / realtime-PAN supersede / BATCH)
// and the tail (ERASE / DSETTINGS / SETVAR / COLOR / draft-session keyword
// gates / geometry-command fallback / catch-all dispatch) stay here,
// unchanged in sequence and behaviour. The large middle chain of one-shot
// dialog/report commands (previously ~50 sequential `if` blocks) is split
// across sibling handler modules — `viewCommands.ts`, `systemCommands.ts`,
// `dynamicCommands.ts`, `blockAuthoringCommands.ts`, `historyCommands.ts`
// — called here in the EXACT original order via a `DispatchResult` chain
// (`null` = not handled, try the next handler; `true`/`false` = handled,
// return immediately), which is a lossless restatement of the original
// sequential `if (cond) { ...; return true|false; }` fall-through chain.
//
// Every free variable the original function closed over is threaded in
// via `SubmitCommandTextDeps` (see `./types.ts`) — App.tsx builds one
// `deps` object once per render and calls `createSubmitCommandText(deps)`
// to obtain the dispatcher function it wires into `submitCommandTextRef`.
// ═══════════════════════════════════════════════════════════════════════════

import {
  ALL_COMMAND_DESCRIPTORS,
  resolveAlias,
} from "@dxf-ai-editor/command-registry";

import { getCeColor } from "../state/cecolorStore";
import {
  describeColor,
  resolveColorPromptReply,
  tryHandleColorCommand,
} from "../dynamicInput/colorCommand";
import { tryHandleSetvarCommand } from "../dynamicInput";
import { isSessionKeywordInput } from "../scene/draftingHelpers";
import {
  isKnownShellKeyboardAlias,
  resolveShellDraftCommand,
} from "../hooks/useCommandLine";

import { handleBlockAuthoringCommands } from "./blockAuthoringCommands";
import { handleDynamicCommands } from "./dynamicCommands";
import { handleHistoryCommands } from "./historyCommands";
import { handleSystemCommands } from "./systemCommands";
import { handleRegenPanCommands, handleZoomCommand } from "./viewCommands";
import { parseVpclipCommandText } from "../dynamicInput/vpclipCommand";
import type { DispatchContext, SubmitCommandTextDeps } from "./types";

export type SubmitCommandTextFn = (
  rawText: string,
  source?: "user" | "batch",
) => boolean;

export function createSubmitCommandText(
  deps: SubmitCommandTextDeps,
): SubmitCommandTextFn {
  return function submitCommandText(
    rawText: string,
    source: "user" | "batch" = "user",
  ): boolean {
    const {
      draftSession,
      colorPromptPending,
      setColorPromptPending,
      dispatch,
      appendCommandFeed,
      t,
      setCommandText,
      panSessionActiveRef,
      endPanSession,
      renderSignalResolversRef,
      submitCommandTextRef,
      selectedEntities,
      setDsettingsOpen,
      openSpecialEditor,
      setCeColorPickerOpen,
      handleDraftTextSubmission,
      beginDraftingWithColorReset,
    } = deps;

    const trimmedText = rawText.trim();

    // FINDING D (round-3) — a pending -COLOR keyword prompt MUST consume the next
    // submission FIRST, before the empty-command guard, BATCH, SETVAR and every
    // other global command gate below. Otherwise an empty Enter (which should
    // accept the `<current>` default) bails at the empty-guard and a command-like
    // reply ("OSMODE 0") is mis-routed to SETVAR instead of being treated as an
    // invalid colour reply (→ re-prompt). With the prompt armed, EVERY next
    // submission — incl. empty Enter and command-like text — routes to
    // `resolveColorPromptReply`.
    if (!draftSession && colorPromptPending) {
      const currentColor = getCeColor();
      const resolved = resolveColorPromptReply(trimmedText, currentColor);
      if (resolved !== null) {
        // Dispatch is the SINGLE source of truth: the reducer writes
        // `doc.cecolor` and `editorStore.dispatch` mirrors it into the live
        // `cecolorStore` (no manual `setCeColor` here — that would be a second
        // truth that diverges on undo / AI).
        dispatch({ type: "header/set-cecolor", source: "commandline", color: resolved });
        setColorPromptPending(false);
        // Feedback via the SAME localized labels the picker / value forms use
        // (not the raw typed text — an empty Enter would otherwise echo "" and a
        // keyword would echo the keyword rather than the resolved colour).
        appendCommandFeed(t("colorcmd.feedback", { value: describeColor(resolved) }));
      } else {
        appendCommandFeed(t("colorcmd.prompt", { current: describeColor(currentColor) }));
      }
      setCommandText("");
      return true;
    }

    if (!trimmedText && !draftSession) {
      return false;
    }

    // cmd-pan FINDING 1 — a realtime-pan session must be torn down the moment
    // ANY OTHER command begins, mirroring the colour-transient supersede pattern
    // (route teardown through the single command-start choke point so the prompt
    // strip + hand cursor never leak into the next command). Without this the
    // first canvas LMB-down of e.g. LINE is consumed as a pan (stageInteractions
    // checks `panSessionActiveRef` BEFORE picking/draft-click) and the user is
    // stuck. The ONE exception is the bare-PAN verb itself — it is the command
    // that STARTS the session, so it must not self-cancel. The scripted
    // displacement `-PAN x,y x,y` form is NOT exempt: a discrete scripted pan
    // legitimately supersedes the interactive session.
    if (panSessionActiveRef.current) {
      const upper = trimmedText.toUpperCase();
      // Bare session-start verb = PAN/-PAN/P/ПАН with NO trailing argument.
      const isBarePanStart = /^(?:-?PAN|P|ПАН)$/.test(upper);
      if (!isBarePanStart) endPanSession();
    }

    // AI_BATCH_COMMANDS: `BATCH ["cmd1","cmd2",...]`
    // Parses a JSON array and runs each entry through submitCommandText.
    // IMPORTANT: Only match when NOT inside a draft session (text content
    // steps may legitimately contain words starting with "Batch"), and only
    // when the payload after "BATCH " actually looks like a JSON array.
    const upperStart = trimmedText.toUpperCase();
    // D5 (App.tsx router→registry unification) — resolve the FULL typed
    // token against the command-registry ONCE here. Every one-shot
    // dialog/system command below (previously a chain of literal
    // `upperStart === "ALIAS"` comparisons per command) now compares against
    // this single canonical id instead of re-deriving alias lists inline.
    // Multi-word / regex-parsed forms (ZOOM sub-options, PAN displacement,
    // LAYOUT sub-verbs) are untouched — resolveAlias only matches a single
    // whole-string token, which is exactly what those literal comparisons
    // also required.
    const resolvedCommandId =
      resolveAlias(upperStart, ALL_COMMAND_DESCRIPTORS)?.id ?? null;
    const looksLikeBatch =
      !draftSession &&
      (resolvedCommandId === "BATCH" ||
        upperStart.startsWith("BATCH [") ||
        upperStart.startsWith("BATCH["));
    if (looksLikeBatch) {
      const jsonPart = trimmedText.slice(5).trim();
      try {
        const parsed = JSON.parse(jsonPart);
        if (!Array.isArray(parsed)) {
          appendCommandFeed(t("feed.batch.expects_array"));
          return false;
        }
        const commands = parsed.map((v) => String(v));
        appendCommandFeed(t("feed.batch.running", { count: commands.length }));
        setCommandText("");
        const waitRender = () =>
          new Promise<void>((resolve) => {
            let settled = false;
            const wrapped = () => {
              if (settled) return;
              settled = true;
              resolve();
            };
            renderSignalResolversRef.current.push(wrapped);
            setTimeout(wrapped, 300);
          });
        void (async () => {
          for (const cmdText of commands) {
            // All sub-commands inside BATCH are treated as "batch" source,
            // which disables ZOOM animation (instant camera jumps).
            submitCommandTextRef.current?.(cmdText, "batch");
            await waitRender();
          }
          appendCommandFeed(
            t("feed.batch.finished", { count: commands.length }),
          );
        })();
        return true;
      } catch (error) {
        appendCommandFeed(
          t("feed.batch.parse_error", {
            error: error instanceof Error ? error.message : String(error),
          }),
        );
        return false;
      }
    }

    const ctx: DispatchContext = {
      ...deps,
      rawText,
      trimmedText,
      upperStart,
      resolvedCommandId,
      source,
    };

    const zoomResult = handleZoomCommand(ctx);
    if (zoomResult !== null) return zoomResult;

    const regenPanResult = handleRegenPanCommands(ctx);
    if (regenPanResult !== null) return regenPanResult;

    const systemResult = handleSystemCommands(ctx);
    if (systemResult !== null) return systemResult;

    const dynamicResult = handleDynamicCommands(ctx);
    if (dynamicResult !== null) return dynamicResult;

    const authoringResult = handleBlockAuthoringCommands(ctx);
    if (authoringResult !== null) return authoringResult;

    const historyResult = handleHistoryCommands(ctx);
    if (historyResult !== null) return historyResult;

    // ERASE — delete selected entities (AutoCAD parity).
    // Resolve the typed token through the command registry so EVERY ERASE
    // alias works (EN: ERASE/E, RU: СТЕРЕТЬ/УДАЛИТЬ — and any future alias)
    // rather than hard-coding string literals here. ERASE is a select-then-act
    // descriptor with no `draftCommandId`, so `normalizeDraftCommand` returns
    // null for it; without this the RU aliases would silently fall through and
    // never dispatch `shell/delete-entities`, contradicting the registry /
    // manifest which advertises the aliases as working. D5 — reuses the
    // SAME `resolvedCommandId` computed once above instead of a second
    // `resolveAlias` call.
    if (
      !draftSession &&
      selectedEntities.length > 0 &&
      resolvedCommandId === "ERASE"
    ) {
      appendCommandFeed(t("feed.delete"));
      dispatch({
        type: "shell/delete-entities",
        source: "commandline",
        entityIds: selectedEntities.map((e) => e.id),
      });
      setCommandText("");
      return true;
    }

    // VPCLIP / ВЭКРЕЗ — scripted viewport clip authoring (VPCLIP-COMMAND, L3).
    // The parser (vpclipCommand.ts) turns `VPCLIP <vp> [P|D|O] …` into a typed
    // viewport-clip command — polygonal/rectangular boundary from points, a clip
    // from a selected object, or a delete — dispatched DIRECTLY (not via the
    // commandline/submit wrapper) so it hits the existing viewport-command
    // dispatch path (pre-mutation snapshot inverse + shell audit + geometry
    // reducer) with no wrapper unwrap. The interactive pick flow is the separate
    // VPCLIP-INTERACTIVE slice; a bare/unparsable `VPCLIP` returns null here and
    // falls through to the generic pipeline. Runs only outside a draft session.
    if (!draftSession) {
      const vpclipCommand = parseVpclipCommandText(trimmedText, "commandline");
      if (vpclipCommand) {
        dispatch(vpclipCommand);
        appendCommandFeed(trimmedText);
        setCommandText("");
        return true;
      }
    }

    // DSETTINGS / DDRMODES / DDRMODE — open the Drafting Settings dialog.
    // Handled before sysvar resolution to avoid a false SETVAR match.
    if (!draftSession && resolvedCommandId === "DSETTINGS") {
      setDsettingsOpen(true);
      setCommandText("");
      return true;
    }

    // REVCLOUDPROPERTIES / ОБЛАКОСВ — open the revision-cloud property editor
    // (CC-3 SpecialEditorHost, REVCLOUD-PROPS-EDITOR §5.1) for the selected
    // revision cloud. Explicit owned routing (A8/E-3): the CLI literal opens the
    // dialog directly. With a Revision Cloud in the selection we open it; without
    // one we prompt the user to select a cloud first (AutoCAD's "select objects"
    // posture — the Properties palette shows nothing to edit otherwise). The host
    // re-gates on `requiresSpecialEditor` so a stray non-cloud id is a no-op.
    if (!draftSession && resolvedCommandId === "REVCLOUDPROPERTIES") {
      const revcloud = selectedEntities.find(
        (entity) => entity.type === "Revision Cloud",
      );
      if (revcloud) {
        openSpecialEditor(revcloud.id);
      } else {
        appendCommandFeed(t("feed.revcloudproperties.select"));
      }
      setCommandText("");
      return true;
    }

    // HATCHEDIT / РЕДШТРИХ / HE — open the "Hatch Edit" dialog (CC-3
    // SpecialEditorHost, HATCH-EDIT-DIALOG §5.1) for a SELECTED hatch, matching
    // AutoCAD where HATCHEDIT on a picked hatch opens the dialog directly (the
    // dialog the existing draft-prompt HATCHEDIT flow's own comment defers to
    // "a separate future PR" — this one). With a Hatch in the selection we open
    // the dialog; with NO hatch selected we DELIBERATELY fall through (no early
    // return) to the existing command-line prompt flow below
    // (`resolveShellDraftCommand` → the select→pattern→scale→angle draft), so
    // that tested command-line variant is preserved. The host re-gates on
    // `requiresSpecialEditor` so a stray non-hatch id is a no-op.
    if (!draftSession && resolvedCommandId === "HATCHEDIT") {
      const hatch = selectedEntities.find(
        (entity) => entity.type === "Hatch",
      );
      if (hatch) {
        openSpecialEditor(hatch.id);
        setCommandText("");
        return true;
      }
      // else: no hatch selected — fall through to the draft prompt flow below.
    }

    // SETVAR / direct sysvar name handling runs BEFORE geometry
    // resolution so typing e.g. "DYNMODE 2" at the command line writes
    // the sysvar instead of falling through to an unknown-command error.
    // Short geometry aliases (L, C, A, PL, …) are explicitly filtered
    // inside tryHandleSetvarCommand so they fall through to the
    // geometry resolver below.
    if (!draftSession) {
      const sysvarResult = tryHandleSetvarCommand(trimmedText);
      if (sysvarResult.handled) {
        appendCommandFeed(sysvarResult.message);
        setCommandText("");
        return true;
      }
    }

    // COLOR / -COLOR / SETCOLOR / ЦВЕТ — CECOLOR current-entity-colour setter.
    // Runs before geometry resolution (like SETVAR). With a value: dispatch the
    // invertible `header/set-cecolor` command. No value: COLOR/SETCOLOR/ЦВЕТ open
    // the Select Color dialog (AciColorPicker); -COLOR runs the dialog-less CLI
    // keyword prompt. The pending -COLOR prompt itself is consumed at the TOP of
    // submitCommandText (FINDING D) — BEFORE the empty-guard / SETVAR / batch
    // gates — so empty Enter + command-like replies both route to the reply
    // resolver. This block only ENTERS / arms the -COLOR prompt.
    if (!draftSession) {
      const colorResult = tryHandleColorCommand(trimmedText, getCeColor());
      if (colorResult.handled) {
        if (colorResult.kind === "set") {
          // Dispatch the invertible command; `editorStore.dispatch` syncs the
          // live `cecolorStore` from `doc.cecolor` (one truth — covers CLI,
          // picker, AI, and undo/redo identically).
          dispatch({
            type: "header/set-cecolor",
            source: "commandline",
            color: colorResult.color,
          });
        } else if (colorResult.kind === "openPicker") {
          setCeColorPickerOpen(true);
        } else if (colorResult.kind === "cliPrompt") {
          // -COLOR dialog-less keyword prompt — arm the pending reply.
          setColorPromptPending(true);
        } else if (colorResult.kind === "invalid") {
          // AutoCAD parity (round-2 FINDING B): "Invalid color." does NOT END the
          // command — it RE-PROMPTS. Arm the dialog-less keyword prompt so the
          // NEXT submission is re-parsed as a colour reply by the
          // `colorPromptPending` consumer above (which dispatches on a valid
          // value or re-prompts again on another invalid / keyword reply). The
          // current colour is unchanged (no dispatch); only the prompt is armed.
          setColorPromptPending(true);
          // Surface the error THEN the prompt on the same turn (AutoCAD shows the
          // "Invalid color." line followed by the re-prompt), so the user sees
          // both the failure and that the command is still awaiting a value.
          appendCommandFeed(colorResult.message);
          appendCommandFeed(
            t("colorcmd.prompt", { current: describeColor(getCeColor()) }),
          );
          setCommandText("");
          return true;
        }
        // Non-invalid handled cases (set / openPicker / cliPrompt): surface the
        // localized message (set echoes the resolved colour; the prompt forms
        // echo the keyword prompt). The `invalid` arm returns above with its own
        // error + re-prompt feed.
        appendCommandFeed(colorResult.message);
        setCommandText("");
        return true;
      }
    }

    // AutoCAD parity: when a draft session is active and the typed
    // text is a known step-keyword (e.g. "L"/"Last"/"All"/Enter at
    // MOVE/COPY/MIRROR/ROTATE/SCALE "Select objects:"; "2P"/"3P"/"Ttr"
    // at CIRCLE's circle-center; "D"/"Displacement" at COPY base-point),
    // route the text into the session's text handler BEFORE attempting
    // to resolve it as a new top-level draft command.
    //
    // Without this gate the single-character shortcut "L" (which
    // shellKeyboardCommandAliases maps to "LINE") destroys an active
    // MOVE/COPY/etc. session by triggering `beginDrafting("LINE")`
    // instead of the AutoCAD-equivalent "select Last entity" behaviour.
    // Same collision risk for "S" (STRETCH) during a SCALE select
    // prompt, "C" (CIRCLE) during a COPY base-point prompt, and so on.
    //
    // We only intercept on known-keyword matches to preserve the
    // long-standing "type a new command at any prompt to override"
    // contract for unrelated input.
    if (draftSession && isSessionKeywordInput(draftSession.step, trimmedText)) {
      if (handleDraftTextSubmission(trimmedText)) {
        setCommandText("");
        return true;
      }
    }

    // AutoCAD parity (FIX-6): single-letter / short-form command aliases
    // (L, M, RO, SC, AR, AE, BE, BS, BC, AP, BH, ML, MV, RC, RV, RO, SC,
    // S, T, B, I, O, H, A, C, …) typed at a sub-prompt of an active
    // draft session must NOT silently start a new top-level command.
    // Real AutoCAD rejects them with "Invalid point" and re-prompts.
    // Without this gate, a user that types "L" at MOVE's
    // `move-base-point` prompt (intending to pick Last entity, ignoring
    // that pre-selection already advanced the session past
    // `move-select`) sees their MOVE session destroyed and replaced by
    // a fresh LINE command.
    //
    // Scope: only block when (a) a draft session is active, (b) the
    // typed text is in the `shellKeyboardCommandAliases` table, AND
    // (c) the text length is ≤ 3 characters. The length cap preserves
    // the legacy "type FULL command name (LINE/MOVE/CIRCLE/…) mid-
    // session to override" UX contract for users who deliberately want
    // to switch without an Esc round-trip. The two paths are
    // distinguished cleanly: short forms (1–3 char) are blocked,
    // full names (4+ chars) still override.
    //
    // D5 follow-up (@codex round-2, Finding 1) — this MUST be the
    // presence-only `isKnownShellKeyboardAlias`, NOT the ambiguity-filtered
    // `isExactShellKeyboardAlias` (that filter exists solely for the
    // no-session canvas keyboard auto-submit path in useKeyboardShortcuts.ts
    // — see the doc comment on both functions in useCommandLine.ts). Using
    // the ambiguity-filtered variant here let short keys with a colliding
    // longer sibling (e.g. "L", ambiguous with LEN/LWPOLYLINE) skip this
    // guard entirely and fall through to `resolveShellDraftCommand` below,
    // destroying the active session instead of being routed to
    // `handleDraftTextSubmission`.
    if (
      draftSession &&
      isKnownShellKeyboardAlias(trimmedText) &&
      trimmedText.trim().length <= 3
    ) {
      if (handleDraftTextSubmission(trimmedText)) {
        setCommandText("");
        return true;
      }
    }

    const normalizedCommand = resolveShellDraftCommand(trimmedText);

    if (normalizedCommand) {
      // AutoCAD parity (Phase H3): re-typing the same command name
      // while a draft session for that command is already running is a
      // no-op (the user is already in the middle of that command).
      // Without this guard, a script that types e.g. "FILLET" again at
      // the fillet-first-entity prompt would destroy the current
      // session — including any sub-flow state like `filletRadius`.
      // Concrete failure mode: the FILLET parity test types "R" + 15
      // to set the radius, then re-types "FILLET" before clicking the
      // entities. The reset wiped `filletRadius=15` back to 0, the
      // commit produced a degenerate sharp-corner with no visible
      // arc, and the test FAILed. Real AutoCAD silently absorbs the
      // re-entry.
      if (draftSession && draftSession.command === normalizedCommand) {
        setCommandText("");
        return true;
      }
      beginDraftingWithColorReset(normalizedCommand);
      setCommandText("");
      return true;
    }

    if (handleDraftTextSubmission(trimmedText)) {
      setCommandText("");
      return true;
    }

    if (!trimmedText) {
      return false;
    }

    dispatch({
      type: "commandline/submit",
      source: "commandline",
      text: rawText,
    });
    appendCommandFeed(trimmedText);
    setCommandText("");
    return true;
  };
}
